/**
 * 用户预设提示词相关API
 */
import { fetchWithRetry } from './utils';
import config from './config';

/**
 * 获取用户的所有预设
 * @param {string} email - 用户邮箱
 * @returns {Promise}
 */
export const getUserPresets = (email) => {
  return fetchWithRetry(
    `${config.API_PATHS.BASE_API_URL}/presets?email=${encodeURIComponent(email)}`,
    {
      method: 'GET',
    }
  );
};

/**
 * 获取当前激活的预设
 * @param {string} email - 用户邮箱
 * @param {string} mode - 可选，当前模式，用于筛选适用的预设
 * @returns {Promise}
 */
export const getActivePreset = (email, mode = null) => {
  let url = `${config.API_PATHS.BASE_API_URL}/presets/active?email=${encodeURIComponent(email)}`;
  if (mode) {
    url += `&mode=${encodeURIComponent(mode)}`;
  }
  return fetchWithRetry(url, {
    method: 'GET',
  });
};

/**
 * 获取预设详情
 * @param {number} presetId - 预设ID
 * @param {string} email - 用户邮箱
 * @returns {Promise}
 */
export const getPresetById = (presetId, email) => {
  return fetchWithRetry(
    `${config.API_PATHS.BASE_API_URL}/presets/${presetId}?email=${encodeURIComponent(email)}`,
    {
      method: 'GET',
    }
  );
};

/**
 * 创建新预设
 * @param {string} email - 用户邮箱
 * @param {Object} presetData - 预设数据
 * @returns {Promise}
 */
export const createPreset = (email, presetData) => {
  return fetchWithRetry(
    `${config.API_PATHS.BASE_API_URL}/presets?email=${encodeURIComponent(email)}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(presetData),
    }
  );
};

/**
 * 更新预设
 * @param {number} presetId - 预设ID
 * @param {string} email - 用户邮箱
 * @param {Object} presetData - 要更新的数据
 * @returns {Promise}
 */
export const updatePreset = (presetId, email, presetData) => {
  return fetchWithRetry(
    `${config.API_PATHS.BASE_API_URL}/presets/${presetId}?email=${encodeURIComponent(email)}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(presetData),
    }
  );
};

/**
 * 删除预设
 * @param {number} presetId - 预设ID
 * @param {string} email - 用户邮箱
 * @returns {Promise}
 */
export const deletePreset = (presetId, email) => {
  return fetchWithRetry(
    `${config.API_PATHS.BASE_API_URL}/presets/${presetId}?email=${encodeURIComponent(email)}`,
    {
      method: 'DELETE',
    }
  );
};

/**
 * 激活指定预设
 * @param {number} presetId - 预设ID
 * @param {string} email - 用户邮箱
 * @returns {Promise}
 */
export const activatePreset = (presetId, email) => {
  return fetchWithRetry(
    `${config.API_PATHS.BASE_API_URL}/presets/${presetId}/activate?email=${encodeURIComponent(email)}`,
    {
      method: 'POST',
    }
  );
};

/**
 * 取消所有预设的激活状态
 * @param {string} email - 用户邮箱
 * @returns {Promise}
 */
export const deactivateAllPresets = (email) => {
  return fetchWithRetry(
    `${config.API_PATHS.BASE_API_URL}/presets/deactivate-all?email=${encodeURIComponent(email)}`,
    {
      method: 'POST',
    }
  );
};

// 预设选项常量
export const PRESET_OPTIONS = {
  gradeLevels: [
    { value: '幼儿园', label: '幼儿园' },
    { value: '小学', label: '小学' },
    { value: '初中', label: '初中' },
    { value: '高中', label: '高中' },
    { value: '大学', label: '大学' },
    { value: '研究生', label: '研究生' },
    { value: '职业教育', label: '职业教育' },
    { value: '__custom__', label: '其他' },
  ],
  subjects: [
    { value: '数学', label: '数学' },
    { value: '物理', label: '物理' },
    { value: '化学', label: '化学' },
    { value: '生物', label: '生物' },
    { value: '语文', label: '语文' },
    { value: '英语', label: '英语' },
    { value: '历史', label: '历史' },
    { value: '地理', label: '地理' },
    { value: '政治', label: '政治' },
    { value: '信息技术', label: '信息技术' },
    { value: '计算机应用', label: '计算机应用' },
    { value: '机电技术应用', label: '机电技术应用' },
    { value: '汽车运用与维修', label: '汽车运用与维修' },
    { value: '电子商务', label: '电子商务' },
    { value: '会计事务', label: '会计事务' },
    { value: '护理', label: '护理' },
    { value: '幼儿保育', label: '幼儿保育' },
    { value: '旅游服务与管理', label: '旅游服务与管理' },
    { value: '烹饪', label: '烹饪' },
    { value: '数控技术应用', label: '数控技术应用' },
    { value: '物流服务与管理', label: '物流服务与管理' },
    { value: '建筑工程施工', label: '建筑工程施工' },
    { value: '__custom__', label: '其他' },
  ],
  styles: [
    { value: '简洁明了', label: '简洁明了' },
    { value: '详细严谨', label: '详细严谨' },
    { value: '生动有趣', label: '生动有趣' },
    { value: '循序渐进', label: '循序渐进' },
    { value: '启发式', label: '启发式' },
  ],
  videoRatios: [
    { value: 'horizontal', label: '横屏 (16:9)' },
    { value: 'vertical', label: '竖屏 (9:16)' },
  ],
  modes: [
    { value: 'manim', label: '动画生成' },
    { value: 'manimgx', label: 'ManimGX动画' },
    { value: 'threejs', label: '3D交互' },
    { value: 'geogebra', label: 'GGb模式' },
    { value: 'solve', label: '智能解题' },
    { value: 'qq', label: '问题拆解' },
  ],
};
